import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminHeader from '../components/AdminHeader';
import axios from 'axios';
import './AdminDashboard.css';

function AdminCustomerConcerns() {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const API_URL = process.env.REACT_APP_API || 'http://localhost:5000';

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/admin/customer-messages?filter=${filter}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setMessages(response.data.messages);
      setFilteredMessages(response.data.messages);
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, filter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);



  const handleMarkAsRead = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/admin/customer-messages/${messageId}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Update local state
      setMessages(prev => prev.map(msg => 
        msg._id === messageId ? { ...msg, isRead: true } : msg
      ));
      if (selectedMessage?._id === messageId) {
        setSelectedMessage(prev => ({ ...prev, isRead: true }));
      }
      fetchMessages(); // Refresh to update unread count
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleDelete = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/admin/customer-messages/${messageId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
      if (selectedMessage?._id === messageId) {
        setSelectedMessage(null);
      }
      setDeleteConfirm(null);
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;
    
    setSendingReply(true);
    try {
      // Send email reply via backend
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/customer-messages/${selectedMessage._id}/reply`,
        { replyText: replyText.trim() },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Update the selected message with the new reply data
      setSelectedMessage(response.data.data);
      // Update in messages list too
      setMessages(prev => prev.map(msg => 
        msg._id === selectedMessage._id ? response.data.data : msg
      ));
      
      setSuccessMessage(`Reply sent successfully to ${selectedMessage.email}!`);
      setShowSuccessModal(true);
      setReplyText('');
      // Mark as read if not already
      if (!selectedMessage.isRead) {
        handleMarkAsRead(selectedMessage._id);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      const errorMessage = error.response?.data?.message 
        || error.message 
        || 'Failed to send reply. Please check your email configuration.';
      alert('Failed to send reply. ' + errorMessage);
    } finally {
      setSendingReply(false);
    }
  };

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    if (!message.isRead) {
      handleMarkAsRead(message._id);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar (thinner) */}
      <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
        {/* Empty green bar for accent */}
      </div>
      {/* Navbar below header, white background */}
      <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
        <div style={{ width: '100%', margin: '0 auto', background: 'transparent' }}>
          <AdminHeader />
        </div>
      </div>
      
      <div style={{ width: '100%', margin: '40px auto', padding: '0 20px', flex: 1, boxSizing: 'border-box', maxWidth: '1400px' }}>
        {/* Header */}
        <div style={{ marginBottom: isMobile ? '20px' : '32px' }}>
          <motion.h2 
            style={{ fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: 700, color: '#222', marginBottom: '8px' }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            Customer Concerns
          </motion.h2>
          <p style={{ color: '#64748b', fontSize: isMobile ? '0.9rem' : '1rem' }}>
            Manage and respond to customer messages
          </p>
        </div>

        {/* Filter Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: isMobile ? '8px' : '12px', 
          marginBottom: isMobile ? '16px' : '24px',
          borderBottom: '2px solid #e2e8f0',
          paddingBottom: '12px',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}>
          <motion.button
            onClick={() => setFilter('all')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              padding: isMobile ? '8px 16px' : '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: filter === 'all' ? '#7a9c3a' : 'transparent',
              color: filter === 'all' ? 'white' : '#64748b',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontSize: isMobile ? '0.9rem' : '1rem',
              whiteSpace: 'nowrap',
              boxShadow: filter === 'all' ? '0 2px 8px rgba(122,156,58,0.08)' : 'none'
            }}>
            All Messages ({messages.length})
          </motion.button>
          <motion.button
            onClick={() => setFilter('unread')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              padding: isMobile ? '8px 16px' : '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: filter === 'unread' ? '#7a9c3a' : 'transparent',
              color: filter === 'unread' ? 'white' : '#64748b',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              position: 'relative',
              fontSize: isMobile ? '0.9rem' : '1rem',
              whiteSpace: 'nowrap',
              boxShadow: filter === 'unread' ? '0 2px 8px rgba(122,156,58,0.08)' : 'none'
            }}>
            Unread
            {unreadCount > 0 && (
              <span style={{
                marginLeft: '8px',
                padding: '2px 8px',
                borderRadius: '12px',
                background: '#dc2626',
                color: 'white',
                fontSize: '0.85rem',
                fontWeight: 700
              }}>
                {unreadCount}
              </span>
            )}
          </motion.button>
          <motion.button
            onClick={() => setFilter('read')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              padding: isMobile ? '8px 16px' : '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: filter === 'read' ? '#7a9c3a' : 'transparent',
              color: filter === 'read' ? 'white' : '#64748b',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontSize: isMobile ? '0.9rem' : '1rem',
              whiteSpace: 'nowrap',
              boxShadow: filter === 'read' ? '0 2px 8px rgba(122,156,58,0.08)' : 'none'
            }}>
            Read
          </motion.button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
            <div style={{ fontSize: '1.2rem' }}>Loading messages...</div>
          </div>
        ) : filteredMessages.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: 'white',
            borderRadius: '16px',
            border: '2px solid #e2e8f0'
          }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="#cbd5e1" style={{ margin: '0 auto 16px' }}>
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
            </svg>
            <div style={{ fontSize: '1.1rem', color: '#64748b', fontWeight: 500 }}>
              No {filter !== 'all' ? filter : ''} messages found
            </div>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile || !selectedMessage ? '1fr' : '1fr 1.5fr',
            gap: isMobile ? '16px' : '24px' 
          }}>
            {/* Messages List */}
            <div style={{ display: isMobile && selectedMessage ? 'none' : 'flex', flexDirection: 'column', gap: '12px' }}>
              <AnimatePresence>
                {filteredMessages.map((message) => (
                  <motion.div
                    key={message._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    onClick={() => handleViewMessage(message)}
                    style={{
                      padding: '20px',
                      background: 'white',
                      borderRadius: '16px',
                      border: `2px solid ${selectedMessage?._id === message._id ? '#7a9c3a' : '#e5e5e5'}`,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      boxShadow: selectedMessage?._id === message._id ? '0 2px 12px rgba(122,156,58,0.15)' : '0 2px 8px rgba(0,0,0,0.04)'
                    }}
                    whileHover={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)', scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}>
                    
                    {!message.isRead && (
                      <div style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: '#dc2626'
                      }} />
                    )}

                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'start',
                      marginBottom: '8px',
                      paddingRight: '20px'
                    }}>
                      <div>
                        <h3 style={{ 
                          fontSize: '1.05rem', 
                          fontWeight: 600, 
                          color: '#1e293b',
                          margin: '0 0 4px 0'
                        }}>
                          {message.name}
                        </h3>
                        <p style={{ 
                          fontSize: '0.9rem', 
                          color: '#64748b',
                          margin: 0
                        }}>
                          {message.email}
                        </p>
                      </div>
                    </div>

                    <p style={{ 
                      fontSize: '0.95rem', 
                      fontWeight: 600, 
                      color: '#475569',
                      margin: '12px 0 8px 0'
                    }}>
                      {message.subject}
                    </p>

                    <p style={{ 
                      fontSize: '0.9rem', 
                      color: '#64748b',
                      margin: '0 0 12px 0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {message.message}
                    </p>

                    <div style={{ 
                      fontSize: '0.85rem', 
                      color: '#94a3b8',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>{formatDate(message.createdAt)}</span>
                      {message.isRead && (
                        <span style={{ 
                          padding: '2px 8px', 
                          borderRadius: '6px', 
                          background: '#f1f5f9',
                          color: '#64748b',
                          fontSize: '0.8rem'
                        }}>
                          Read
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Message Detail Panel */}
            {selectedMessage && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                style={{
                  padding: isMobile ? '20px' : '32px',
                  background: 'white',
                  borderRadius: '16px',
                  border: '2px solid #e2e8f0',
                  height: 'fit-content',
                  position: isMobile ? 'relative' : 'sticky',
                  top: isMobile ? '0' : '24px'
                }}>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'start',
                  marginBottom: isMobile ? '16px' : '24px',
                  paddingBottom: isMobile ? '16px' : '20px',
                  borderBottom: '2px solid #f1f5f9'
                }}>
                  <div style={{ flex: 1, minWidth: 0, paddingRight: '12px' }}>
                    <h2 style={{ 
                      fontSize: isMobile ? '1.25rem' : '1.5rem', 
                      fontWeight: 700, 
                      color: '#1e293b',
                      margin: '0 0 8px 0',
                      wordBreak: 'break-word'
                    }}>
                      {selectedMessage.subject}
                    </h2>
                    <p style={{ 
                      fontSize: isMobile ? '0.85rem' : '0.95rem', 
                      color: '#64748b',
                      margin: 0,
                      wordBreak: 'break-word'
                    }}>
                      From: <strong>{selectedMessage.name}</strong> ({selectedMessage.email})
                    </p>
                    <p style={{ 
                      fontSize: '0.9rem', 
                      color: '#94a3b8',
                      margin: '4px 0 0 0'
                    }}>
                      {formatDate(selectedMessage.createdAt)}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedMessage(null)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '1.5rem',
                      color: '#64748b',
                      cursor: 'pointer',
                      padding: '4px 8px'
                    }}>
                    ×
                  </button>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ 
                    fontSize: '0.9rem', 
                    fontWeight: 600, 
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    Original Message
                  </h3>
                  <div style={{ 
                    background: '#f8fdf9',
                    padding: '16px',
                    borderRadius: '8px',
                    borderLeft: '4px solid #7a9c3a'
                  }}>
                    <p style={{ 
                      fontSize: '1rem', 
                      color: '#334155',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      margin: 0
                    }}>
                      {selectedMessage.message}
                    </p>
                  </div>
                </div>

                {/* Conversation History */}
                {selectedMessage.replies && selectedMessage.replies.length > 0 && (
                  <div style={{ marginBottom: '32px' }}>
                    <h3 style={{ 
                      fontSize: '0.9rem', 
                      fontWeight: 600, 
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '12px'
                    }}>
                      Conversation History ({selectedMessage.replies.length})
                    </h3>
                    <div style={{ 
                      maxHeight: '300px',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      {selectedMessage.replies.map((reply, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          style={{
                            background: '#eef7e9',
                            padding: '14px 16px',
                            borderRadius: '8px',
                            borderLeft: '3px solid #7a9c3a'
                          }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px'
                          }}>
                            <span style={{ 
                              fontSize: '0.85rem', 
                              fontWeight: 600,
                              color: '#7a9c3a'
                            }}>
                              {reply.sentBy}
                            </span>
                            <span style={{ 
                              fontSize: '0.75rem', 
                              color: '#94a3b8'
                            }}>
                              {formatDate(reply.sentAt)}
                            </span>
                          </div>
                          <p style={{ 
                            fontSize: '0.95rem', 
                            color: '#334155',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                            margin: 0
                          }}>
                            {reply.text}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reply Section */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ 
                    fontSize: '0.9rem', 
                    fontWeight: 600, 
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    Send Reply
                  </h3>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply to the customer..."
                    style={{
                      width: '100%',
                      minHeight: '120px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      fontSize: '0.95rem',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color 0.2s',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#7a9c3a'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                  <motion.button
                    onClick={handleSendReply}
                    disabled={!replyText.trim() || sendingReply}
                    whileHover={replyText.trim() && !sendingReply ? { scale: 1.02, backgroundColor: '#5e7c2a' } : {}}
                    whileTap={replyText.trim() && !sendingReply ? { scale: 0.98 } : {}}
                    style={{
                      marginTop: '12px',
                      padding: '10px 24px',
                      background: !replyText.trim() || sendingReply ? '#ccc' : '#7a9c3a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: !replyText.trim() || sendingReply ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: !replyText.trim() || sendingReply ? 'none' : '0 2px 8px rgba(122,156,58,0.08)',
                      width: '100%'
                    }}>
                    {sendingReply ? 'Sending...' : '📧 Send Reply to Customer'}
                  </motion.button>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {!selectedMessage.isRead && (
                    <motion.button
                      onClick={() => handleMarkAsRead(selectedMessage._id)}
                      whileHover={{ scale: 1.02, backgroundColor: '#5e7c2a' }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        flex: 1,
                        minWidth: '140px',
                        padding: '12px 24px',
                        background: '#7a9c3a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 8px rgba(122,156,58,0.08)'
                      }}>
                      Mark as Read
                    </motion.button>
                  )}
                  <motion.button
                    onClick={() => setDeleteConfirm(selectedMessage._id)}
                    whileHover={{ scale: 1.02, backgroundColor: '#c62828' }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      flex: 1,
                      minWidth: '140px',
                      padding: '12px 24px',
                      background: '#d32f2f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(211,47,47,0.08)'
                    }}>
                    Delete Message
                  </motion.button>
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* Success Modal */}
        <AnimatePresence>
          {showSuccessModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px'
              }}
              onClick={() => setShowSuccessModal(false)}>
              
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '32px',
                  maxWidth: '400px',
                  width: '100%',
                  textAlign: 'center'
                }}>
                
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #7a9c3a 0%, #88ab47 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '32px'
                }}>
                  ✓
                </div>

                <h3 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 700, 
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  Reply Sent!
                </h3>
                <p style={{ 
                  fontSize: '1rem', 
                  color: '#64748b',
                  marginBottom: '24px',
                  lineHeight: 1.6
                }}>
                  {successMessage}
                </p>
                
                <motion.button
                  onClick={() => setShowSuccessModal(false)}
                  whileHover={{ scale: 1.02, backgroundColor: '#5e7c2a' }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    background: '#7a9c3a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '1rem',
                    boxShadow: '0 2px 8px rgba(122,156,58,0.08)'
                  }}>
                  OK
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {deleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px'
              }}
              onClick={() => setDeleteConfirm(null)}>
              
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '32px',
                  maxWidth: '400px',
                  width: '100%'
                }}>
                
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 700, 
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  Delete Message?
                </h3>
                <p style={{ 
                  fontSize: '1rem', 
                  color: '#64748b',
                  marginBottom: '24px'
                }}>
                  This action cannot be undone. Are you sure you want to delete this message?
                </p>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <motion.button
                    onClick={() => setDeleteConfirm(null)}
                    whileHover={{ scale: 1.02, backgroundColor: '#e8e8e8' }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: '#f5f5f5',
                      color: '#475569',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}>
                    Cancel
                  </motion.button>
                  <motion.button
                    onClick={() => handleDelete(deleteConfirm)}
                    whileHover={{ scale: 1.02, backgroundColor: '#c62828' }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: '#d32f2f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 8px rgba(211,47,47,0.08)'
                    }}>
                    Delete
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Green Footer Bar */}
      <div style={{ width: '100%', background: '#175c36', minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 32 }}>
        <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 500, letterSpacing: 1 }}>© {new Date().getFullYear()} Chiqui's Garden</span>
      </div>
    </div>
  );
}

export default AdminCustomerConcerns;
